"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TrendingUp, Search, Target, MapPin, ArrowRight, ChartBar as BarChart3, CircleCheck as CheckCircle2, Building2, Compass } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
const searchData = [{
  month: "Jan",
  searchVolume: 12400,
  competitorVal: 8200
}, {
  month: "Feb",
  searchVolume: 13800,
  competitorVal: 8400
}, {
  month: "Mar",
  searchVolume: 15100,
  competitorVal: 8900
}, {
  month: "Apr",
  searchVolume: 16500,
  competitorVal: 9100
}, {
  month: "May",
  searchVolume: 18200,
  competitorVal: 9500
}, {
  month: "Jun",
  searchVolume: 21500,
  competitorVal: 9800
}];
const metrics = [{
  title: "Preston Monthly Search Volume",
  value: "24,500+",
  subtitle: "High-intent queries for local B2B, professional & commercial services.",
  icon: Search
}, {
  title: "Google Map Pack Deficit",
  value: "38%",
  subtitle: "Local Preston businesses missing technical schema & citation depth.",
  icon: Target
}, {
  title: "Organic Opportunity Index",
  value: "62%",
  subtitle: "Market share available through structured content & local authority.",
  icon: TrendingUp
}];
const opportunities = [{
  title: "Winckley Square & Docklands Focus",
  desc: "Optimise localized search strategies targeting Preston's central financial district, tech hubs, and commercial industrial estates."
}, {
  title: "Lancashire Regional Authority Building",
  desc: "Strengthen regional entity relevance across Lancashire to capture surrounding search volume in Ribble Valley, South Ribble, and Fylde."
}, {
  title: "AI Search & Zero-Click Visibility",
  desc: "Structure your Preston business for AI Overview snapshots and Google Business Profile triggers before prospects reach conventional listings."
}];
const nearbyLocations = [{
  name: "Wakefield",
  path: "/LocalSeoConsultantWakefieldPage",
  distance: "48 miles"
}, {
  name: "Warrington",
  path: "/LocalSeoConsultantWarringtonPage",
  distance: "32 miles"
}, {
  name: "Stoke on Trent",
  path: "/LocalSeoConsultantStokeOnTrentPage",
  distance: "55 miles"
}, {
  name: "Derby",
  path: "/LocalSeoConsultantDerbyPage",
  distance: "72 miles"
}, {
  name: "Northampton",
  path: "/LocalSeoConsultantNorthamptonPage",
  distance: "135 miles"
}, {
  name: "Peterborough",
  path: "/LocalSeoConsultantPeterboroughPage",
  distance: "140 miles"
}];
const chartConfig = {
  searchVolume: {
    label: "Total Search Volume",
    color: "hsl(220 80% 30%)"
  },
  competitorVal: {
    label: "Competitor Capture",
    color: "hsl(340 75% 65%)"
  }
};
export default function Preston() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="prestonmarketanalysis" data-nav="light" className="bg-background text-foreground py-16 md:py-24 transition-colors">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="max-w-3xl space-y-4 mb-12">
          <Badge variant="outline" className="px-3 py-1 text-xs md:text-sm border-primary/20 text-primary bg-primary/5 rounded-full">
            <MapPin className="size-3.5 mr-1 text-accent" />
            Lancashire Search Intelligence
          </Badge>

          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            Preston SEO &amp; Search Market Analysis
          </h2>

          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            I analyse Preston search intent directly to give your business an undeniable edge.
            While many Preston businesses rely on generic tactics, I map real Lancashire search behavior, keyword growth, and competitor weaknesses so you capture local leads efficiently.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {metrics.map((item, i) => {
          const IconComponent = item.icon;
          return <motion.div key={item.title} variants={itemVariants} data-index={i}>
                <Card className="border-border bg-card shadow-sm hover:shadow-md transition-[box-shadow,border-color] h-full">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {item.title}
                    </CardTitle>
                    <div className="p-2 rounded-md bg-secondary text-secondary-foreground">
                      <IconComponent className="size-4 text-primary" />
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-1">
                    <div className="text-3xl font-bold text-foreground font-mono">{item.value}</div>
                    <CardDescription className="text-xs text-muted-foreground leading-normal">
                      {item.subtitle}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-16">
          <Card className="lg:col-span-7 border-border bg-card shadow-sm p-4 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div>
                <h3 className="text-xl font-semibold text-foreground font-serif flex items-center gap-2">
                  <BarChart3 className="size-5 text-primary" />
                  Preston Search Demand vs. Market Capture
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Monthly growth of localized commercial search volume in Preston (PR1 - PR5).
                </p>
              </div>
              <Badge variant="secondary" className="w-fit text-xs font-mono">
                PR1 - PR5 Focus
              </Badge>
            </div>

            <div className="w-full">
              <ChartContainer config={chartConfig} className="h-72 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={searchData} margin={{
                  top: 10,
                  right: 10,
                  left: -20,
                  bottom: 0
                }}>
                    <XAxis dataKey="month" stroke="currentColor" className="text-muted-foreground text-xs" />
                    <YAxis stroke="currentColor" className="text-muted-foreground text-xs" />
                    <ChartTooltip content={<ChartTooltipContent indicator="dot" />} />
                    <Area type="monotone" dataKey="searchVolume" stroke="var(--color-searchVolume)" fill="var(--color-searchVolume)" fillOpacity={0.15} strokeWidth={2} />
                    <Area type="monotone" dataKey="competitorVal" stroke="var(--color-competitorVal)" fill="var(--color-competitorVal)" fillOpacity={0.1} strokeWidth={2} strokeDasharray="4 4" />
                  </AreaChart>
                </ResponsiveContainer>
              </ChartContainer>
            </div>

            <div className="flex items-center justify-center gap-6 mt-4 pt-4 border-t border-border text-xs text-muted-foreground">
              <div className="flex items-center gap-2">
                <span className="size-2.5 rounded-full bg-primary inline-block" />
                <span>Total Local Intent</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="size-2.5 rounded-full bg-accent inline-block" />
                <span>Current Competitor Reach</span>
              </div>
            </div>
          </Card>

          <div className="lg:col-span-5 space-y-6">
            <div className="p-6 rounded-xl bg-muted/60 border border-border space-y-4">
              <h3 className="text-xl font-semibold text-foreground font-serif flex items-center gap-2">
                <Building2 className="size-5 text-primary" />
                Preston Growth Opportunities
              </h3>
              <p className="text-xs text-muted-foreground">
                My direct analysis highlights key areas where Preston businesses can capture market share rapidly without wasted ad spend.
              </p>

              <div className="space-y-4 pt-2">
                {opportunities.map((opp, i) => <div key={opp.title} className="flex items-start gap-3" data-index={i}>
                    <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                    <div className="space-y-1">
                      <h4 className="text-sm font-semibold text-foreground">{opp.title}</h4>
                      <p className="text-xs text-muted-foreground leading-relaxed">{opp.desc}</p>
                    </div>
                  </div>)}
              </div>

              <div className="pt-4 border-t border-border">
                <Link to="/ContactPage">
                  <Button variant="default" className="w-full justify-between group">
                    <span>Book Your Preston SEO Consultation</span>
                    <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-6 md:p-8 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 border-b border-border">
            <div>
              <h3 className="text-lg md:text-xl font-semibold text-foreground font-serif flex items-center gap-2">
                <Compass className="size-5 text-accent" />
                Expanding Your Reach Across Lancashire &amp; The North West
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground mt-1">
                Local SEO does not stop at Preston city borders. Explore my tailored search strategies for neighboring hubs:
              </p>
            </div>
            <Link to="/LocalSEOPage">
              <Button variant="outline" size="sm" className="whitespace-nowrap">
                All Local SEO Locations
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
            {nearbyLocations.map((loc, i) => <div key={loc.name} data-index={i}>
                <Link to={loc.path} className="block group">
                  <div className="p-3 rounded-lg border border-border/80 bg-background hover:bg-secondary/50 hover:border-primary/30 transition-all text-center space-y-1">
                    <span className="text-xs font-medium text-foreground group-hover:text-primary transition-colors block truncate">
                      {loc.name}
                    </span>
                    <span className="text-[11.5px] text-muted-foreground block font-mono">
                      {loc.distance}
                    </span>
                  </div>
                </Link>
              </div>)}
          </div>
        </div>
      </div>
    </section>;
}